import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { ReplacementGatesSummary } from "../../../hooks/useCallOffReplacementGate";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import { replacementChoiceVariants } from "../../../utils/callOffReplacementAdvice";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import SystemDialog from "../../UIToolkit/Popups/Dialog";

export type CallOffChoiceDialogProps = {
	open: boolean;
	/** Staff whose shifts start inside the call-off window, in pick order — the ones the choice is about. */
	inWindowNames: readonly string[];
	/** Picks outside the window, removed without a search whatever is chosen here. */
	outsideWindowCount: number;
	gates: Pick<ReplacementGatesSummary, "isReady" | "recommendation" | "message">;
	submitting: boolean;
	onSearch: () => void;
	onRemoveOnly: () => void;
	onCancel: () => void;
};

/** What the choice covers: the one person, or how many of the picks it applies to — and what happens to the rest. */
const ChoiceBody: FC<Pick<CallOffChoiceDialogProps, "inWindowNames" | "outsideWindowCount" | "gates">> = ({ inWindowNames, outsideWindowCount, gates }) => {
	const { t } = useTranslation();
	const count = inWindowNames.length;
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY }}>
				{count === 1 ? t("remove.staff.call.off.description.one", { name: inWindowNames[0] }) : t("remove.staff.call.off.description.many", { count })}
			</Typography>
			{outsideWindowCount > 0 && (
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }} data-testid="remove-staff-call-off-others">
					{t("remove.staff.call.off.others", { count: outsideWindowCount })}
				</Typography>
			)}
			{/* No tick here, so the note is the recommendation itself — shown whichever way it points. */}
			{gates.message && (
				<InlineHint tone={gates.message.tone} dataTestId="remove-staff-call-off-hint">
					{t(gates.message.messageKey, { count })}
				</InlineHint>
			)}
		</Box>
	);
};

/**
 * One "find a replacement?" choice for every picked shift that starts inside the department's call-off window.
 * The recommended answer is emphasised — neither when coverage couldn't be checked or the checks haven't
 * answered — and searching waits for those checks. "Just remove" removes them like any other shift. Back
 * returns to the panel with nothing removed.
 */
export const CallOffChoiceDialog: FC<CallOffChoiceDialogProps> = ({
	open,
	inWindowNames,
	outsideWindowCount,
	gates,
	submitting,
	onSearch,
	onRemoveOnly,
	onCancel,
}) => {
	const { t } = useTranslation();
	const count = inWindowNames.length;
	const variants = replacementChoiceVariants(gates.recommendation);
	return (
		<SystemDialog
			open={open}
			onClose={onCancel}
			title={t("remove.staff.call.off.title", { count })}
			body={<ChoiceBody inWindowNames={inWindowNames} outsideWindowCount={outsideWindowCount} gates={gates} />}
			confirm={t("remove.staff.call.off.search", { count })}
			onConfirm={onSearch}
			confirmButtonVariant={variants.search}
			confirmDisabled={!gates.isReady || submitting}
			confirmLoading={submitting}
			secondaryAction={{
				label: t("remove.staff.call.off.remove.only"),
				onClick: onRemoveOnly,
				disabled: submitting,
				buttonVariant: variants.removeOnly,
			}}
			cancel={t("remove.staff.call.off.back")}
			dataTestId="remove-staff-call-off-dialog"
			confirmDataTestId="remove-staff-call-off-confirm"
			cancelDataTestId="remove-staff-call-off-cancel"
		/>
	);
};
